import React, { useState } from 'react';
import { apiService } from '../api/apiService';
import { useNotification } from '../context/NotificationContext';
import { useTaskPolling } from '../context/TaskPollingContext';

const UnifiedUpload = () => {
  const { addToast } = useNotification();
  const { registerTask } = useTaskPolling();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [username, setUsername] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [projectNotes, setProjectNotes] = useState('');
  const [backgroundTasks, setBackgroundTasks] = useState(null);

  const handleSingleFileChange = (e) => {
    setUploadedFile(e.target.files[0]);
  };

  const handleMultipleFilesChange = (e) => {
    setUploadedFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Determine what type of processing to do based on the inputs provided
    const hasCodebase = uploadedFile !== null;
    const hasDocuments = uploadedFiles.length > 0;
    const hasNotes = projectNotes.trim() !== '';

    // At least one input type and both project name and username are required
    if ((!hasCodebase && !hasDocuments && !hasNotes) || !projectName || !username) {
      setError('Please provide at least one of: codebase ZIP, document files, or project notes, along with project name and username');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    // Determines which endpoint to call based on the inputs provided
    try {
      const formData = new FormData();
      formData.append('project_name', projectName);
      formData.append('username', username);

      if (hasCodebase) {
        formData.append('codebase_file', uploadedFile);
      }

      if (hasDocuments) {
        uploadedFiles.forEach(file => {
          formData.append('document_files', file);
        });
      }

      if (hasNotes) {
        formData.append('project_notes', projectNotes);
      }

      const response = await apiService.processContent(formData);
      setUploadResult(response);

      // Kick off background task polling and create toasts if task IDs were returned
      if (response.tasks) {
        setBackgroundTasks(response.tasks);

        // Create toast notifications for each task
        const newToastIds = {};

        Object.entries(response.tasks).forEach(([key, id]) => {
          if (id) {
            // Create toast for this task
            const toastId = addToast({
              title: projectName,
              message: `Starting ${key.replace('_', ' ')}...`,
              type: 'info',
              status: 'running',
              progress: 0,
            });
            newToastIds[key] = toastId;

            // Register task with global polling service
            registerTask(id, toastId, projectName, key);
          }
        });
      }

      // Clear inputs on success
      setUploadedFile(null);
      setUploadedFiles([]);
      setProjectName('');
      setUsername('');
      setProjectNotes('');

    } catch (err) {
      // Handle specific error messages from the backend
      let errorMessage = '';

      if (err.message) {
        // Check if it's a JSON error response from the backend
        if (err.message.includes('HTTP error! status:') && err.message.includes('"detail"')) {
          try {
            // Extract the detail from the JSON response
            const startIndex = err.message.indexOf('"detail":"') + '"detail":"'.length;
            const endIndex = err.message.indexOf('"}', startIndex);

            if (startIndex !== -1 && endIndex !== -1) {
              errorMessage = err.message.substring(startIndex, endIndex);
              // Remove any escaped quotes
              errorMessage = errorMessage.replace(/\\"/g, '"');
            } else {
              // If we can't extract the detail, use a generic message
              errorMessage = 'An error occurred during processing. Please try again.';
            }
          } catch (parseErr) {
            errorMessage = 'An error occurred during processing. Please try again.';
          }
        } else if (err.message.includes('already exists')) {
          errorMessage = 'Project name already exists. Please choose a different project name.';
        } else {
          errorMessage = err.message;
        }
      } else {
        errorMessage = 'An error occurred during processing. Please try again.';
      }

      setError(errorMessage);
    }

    setIsUploading(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Unified Processing</h2>

      <p className="mb-4 text-gray-600">Upload your codebase, documents, or project notes. The system will automatically determine the appropriate processing method based on your inputs.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Codebase ZIP
          </label>
          <input
            type="file"
            accept=".zip"
            onChange={handleSingleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
          <p className="mt-1 text-sm text-gray-500">Only .zip files are accepted.</p>

          {uploadedFile && (
            <div className="mt-2 p-2 bg-gray-50 rounded border">
              <p className="text-sm text-gray-600">Selected file: {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(2)} KB)</p>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Documents
          </label>
          <input
            type="file"
            accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.json"
            onChange={handleMultipleFilesChange}
            multiple
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
          <p className="mt-1 text-sm text-gray-500">Upload one or more document files to process and extract features.</p>

          {uploadedFiles.length > 0 && (
            <div className="mt-2 p-2 bg-gray-50 rounded border">
              <p className="text-sm font-medium text-gray-700">Selected files:</p>
              <ul className="mt-1 text-sm text-gray-600">
                {uploadedFiles.map((file, index) => (
                  <li key={index} className="truncate">{file.name} ({(file.size / 1024).toFixed(2)} KB)</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label htmlFor="projectNotes" className="block text-sm font-medium text-gray-700 mb-2">
            Project Notes
          </label>
          <textarea
            id="projectNotes"
            value={projectNotes}
            onChange={(e) => setProjectNotes(e.target.value)}
            placeholder="Paste your project notes here (will be treated as Markdown)..."
            rows="6"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          />
          <p className="mt-1 text-sm text-gray-500">Enter your project notes in Markdown format. These will be processed to extract features and attributes.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name for the combined analysis"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Your Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isUploading}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
          >
            {isUploading ? 'Processing...' : 'Execute'}
          </button>

          {/* Inline status display while processing */}
          {backgroundTasks && isUploading && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-center">
              <p className="text-sm text-blue-700 font-medium">⏳ Processing started. Check notifications in the top-right corner for status updates.</p>
            </div>
          )}
        </div>
      </form>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {uploadResult && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <h3 className="font-medium text-green-800 mb-1">✓ Upload Successful!</h3>
          <p className="text-sm text-green-700">
            Files uploaded. Background processing is running — check the top-right corner for live status updates.
          </p>
        </div>
      )}


      {uploadResult && uploadResult.token_usage && (
        <div className="mt-3">
          <h4 className="font-medium text-gray-800">Token Usage Summary</h4>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {(() => {
              let totalOutput = 0;
              let totalTokens = 0;
              for (const usage of Object.values(uploadResult.token_usage)) {
                if (usage && typeof usage === 'object') {
                  totalOutput += usage.output_tokens || 0;
                  totalTokens += usage.total_tokens || 0;
                }
              }
              return (
                <>
                  <div className="p-3 bg-blue-50 rounded-md text-center">
                    <p className="text-sm text-gray-600">Total Output Tokens</p>
                    <p className="text-lg font-semibold text-gray-900">{totalOutput.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-md text-center">
                    <p className="text-sm text-gray-600">Total Tokens</p>
                    <p className="text-lg font-semibold text-gray-900">{totalTokens.toLocaleString()}</p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedUpload;